import React from 'react';
import { 
  Box, 
  Text, 
  Progress, 
  Flex, 
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  HStack
} from '@chakra-ui/react';
import { 
  CloudArrowUp, 
  MagnifyingGlass, 
  WarningCircle,
  Cube
} from '@phosphor-icons/react';

const MediaUploadProgress = ({ currentStep, uploadProgress, error, fileType }) => {
  if (currentStep === 'idle' && !error) return null;
  
  // Theme colors
  const progressTrackBg = useColorModeValue('gray.100', 'gray.700');
  const progressFilledBg = useColorModeValue('blue.500', 'blue.300');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const alertBg = useColorModeValue('red.50', 'red.900');
  const alertBorderColor = useColorModeValue('red.200', 'red.700');
  const alertTextColor = useColorModeValue('red.700', 'red.200');
  const progressBg = useColorModeValue('blue.50', 'blue.900');
  const progressBorderColor = useColorModeValue('blue.100', 'blue.700');

  return (
    <>
      {currentStep !== 'idle' && (
        <Box 
          p={4} 
          borderRadius="md" 
          bg={progressBg}
          borderWidth="1px"
          borderColor={progressBorderColor}
        >
          <HStack spacing={3} mb={3}>
            {currentStep === 'detecting' ? (
              <>
                <MagnifyingGlass size={20} weight="duotone" color={progressFilledBg} />
                <Text fontWeight="medium" color={textColor}>
                  Analyzing image...
                </Text>
              </>
            ) : fileType === '3d' ? (
              <>
                <Cube size={20} weight="duotone" color={progressFilledBg} />
                <Text fontWeight="medium" color={textColor}>
                  Uploading 3D model... {uploadProgress > 0 ? `${Math.round(uploadProgress)}%` : ''}
                </Text>
              </>
            ) : (
              <>
                <CloudArrowUp size={20} weight="duotone" color={progressFilledBg} />
                <Text fontWeight="medium" color={textColor}>
                  Uploading image... {uploadProgress > 0 ? `${Math.round(uploadProgress)}%` : ''}
                </Text>
              </>
            )}
          </HStack>
          
          <Progress
            value={currentStep === 'uploading' ? uploadProgress : undefined}
            isIndeterminate={currentStep === 'detecting'}
            colorScheme="blue"
            size="sm"
            borderRadius="full"
            bg={progressTrackBg}
            sx={{
              '& > div': {
                background: progressFilledBg
              }
            }}
          />
        </Box>
      )}

      {error && (
        <Alert 
          status="error" 
          variant="subtle" 
          borderRadius="md" 
          bg={alertBg}
          borderWidth="1px"
          borderColor={alertBorderColor}
          color={alertTextColor}
          mt={currentStep !== 'idle' ? 4 : 0}
        >
          <AlertIcon as={WarningCircle} weight="duotone" />
          <Box>
            <AlertTitle fontWeight="medium">Upload Error</AlertTitle>
            <AlertDescription fontSize="sm">{error}</AlertDescription>
          </Box>
        </Alert>
      )}
    </>
  );
};

export default MediaUploadProgress;
